import { computed, Injectable, signal } from '@angular/core';
import { form, required } from '@angular/forms/signals';
import { UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import {
  CreateSocialScreeningBriefDto,
  ScreeningListCreatorItem,
  ScreeningListResponseDto,
  ScreeningReportType,
  ScreeningRestrictionItem,
} from '../../../../common/general-types/social-screening.dto';
import {
  linesToHtmlList,
  proseToHtml,
  restrictionKey,
} from '../../../screening-brief-select/screening-brief.utils';
import { CUSTOM_RESTRICTION_CATEGORY_ID } from '../../../screening-brief-select/screening-brief.constants';

/** A restriction while it is being edited: the wire shape plus a client-only row id. */
export type WizardRestriction = ScreeningRestrictionItem & { id: string };

/** How the brand chose to source creators in the wizard's final step. */
export type CreatorSourceMethod = 'one' | 'csv' | 'shout' | 'later';

@Injectable({
  providedIn: 'root'
})
export class CreateScreeningService {


  /** Basic Info Values */
  basicInfoModel = signal({
    briefTitle: '',
  })
  basicInfoForm = form(this.basicInfoModel, (schemaPath) => {
    required(schemaPath.briefTitle, { message: 'Brief title is required' });
  })

  selectedProduct = signal<UgcProductResponseDto | undefined>(undefined);

  additionalProductImages = signal<string[]>([]);
  /** Basic Info Values  - END */

  /** How the brand chose to define creative direction (wizard method chooser). */
  creativeMethod = signal<'upload' | 'type' | 'template' | 'ai'>('type');

  /** Requirements Values */
  requirementsModel = signal({
    requirementsGoogleDocUrl: '',
  })
  requirementsForm = form(this.requirementsModel, (schemaPath) => {
    required(schemaPath.requirementsGoogleDocUrl, { message: 'Requirements Google Doc URL is required' });
  })
  requirementsHtml = signal<string>('');
  requirementsDocumentUrl = signal<string>('');
  screenContentUntil = signal<Date | undefined>(undefined);
  /** Requirements Values - END */

  /** Brief Text Values */
  briefTextModel = signal({
    requirementsGoogleDocUrl: '',
  })
  briefTextForm = form(this.briefTextModel, (schemaPath) => {
    required(schemaPath.requirementsGoogleDocUrl, { message: 'Requirements Google Doc URL is required' });
  })
  briefTextHtml = signal<string>('');
  /** Brief Text Values - END */

  /** Restrictions Values */
  restrictionsModel = signal({
    restrictionsGoogleDocUrl: '',
  })
  restrictionsForm = form(this.restrictionsModel, (schemaPath) => {
    required(schemaPath.restrictionsGoogleDocUrl, { message: 'Restrictions Google Doc URL is required' });
  })
  restrictionsHtml = signal<string>('');
  restrictionsDocumentUrl = signal<string>('');
  /** Restrictions Values - END */

  /** Screening wizard — step 2: the positives, as free prose. */
  lookFor = signal<string>('');

  /** Screening wizard — step 3: the content no-go list. */
  restrictions = signal<WizardRestriction[]>([]);

  /** Screening wizard — step 4: how results come back. */
  reportType = signal<ScreeningReportType | undefined>(undefined);
  reportPrompt = signal<string>('');

  /** Screening wizard — step 6. Distinct from `creativeMethod`, which is the step-3 editor mode. */
  creatorSourceMethod = signal<CreatorSourceMethod | undefined>(undefined);
  /** Creators typed in one by one; turned into a screening list on submit. */
  manualCreators = signal<ScreeningListCreatorItem[]>([]);

  /** True once the pre-flight check passes, or the user chooses "Continue anyway". */
  checkPassed = signal<boolean>(false);

  /** Lookup of `categoryId::text` keys, so pill state is a Set hit rather than a scan. */
  readonly restrictionKeys = computed<Set<string>>(
    () => new Set(this.restrictions().map((item) => restrictionKey(item.categoryId, item.text))),
  );

  /** Per-category counts for the category rail badges. */
  readonly restrictionCounts = computed<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    for (const item of this.restrictions()) {
      counts[item.categoryId] = (counts[item.categoryId] ?? 0) + 1;
    }
    return counts;
  });

  hasRestriction(categoryId: string, text: string): boolean {
    return this.restrictionKeys().has(restrictionKey(categoryId, text));
  }

  /** Adds a rule unless it is blank or already present for that category. */
  addRestriction(categoryId: string, categoryLabel: string, text: string, custom = false): void {
    const trimmed = text.trim();
    if (!trimmed || this.hasRestriction(categoryId, trimmed)) {
      return;
    }
    const item: WizardRestriction = {
      id: crypto.randomUUID(),
      categoryId,
      categoryLabel,
      text: trimmed,
      custom: custom || categoryId === CUSTOM_RESTRICTION_CATEGORY_ID,
    };
    this.restrictions.update((current) => [...current, item]);
  }

  removeRestriction(id: string): void {
    this.restrictions.update((current) => current.filter((item) => item.id !== id));
  }

  toggleRestriction(categoryId: string, categoryLabel: string, text: string): void {
    const trimmed = text.trim();
    const existing = this.restrictions().find(
      (item) => restrictionKey(item.categoryId, item.text) === restrictionKey(categoryId, trimmed),
    );
    if (existing) {
      this.removeRestriction(existing.id);
      return;
    }
    this.addRestriction(categoryId, categoryLabel, trimmed);
  }

  /**
   * The HTML written to the brief's `contentDonts`. The wizard's structured rules win; the AI-first
   * flow and the legacy requirements editor still write `restrictionsHtml` directly, so that is the
   * fallback. The manage-brief screen renders this with `[innerHTML]`.
   */
  readonly contentDontsHtml = computed<string>(() => {
    const list = linesToHtmlList(this.restrictions().map((item) => item.text));
    return list || this.restrictionsHtml();
  });

  /** The HTML written to the brief's `contentRequirements`. Same fallback rule as above. */
  readonly contentRequirementsHtml = computed<string>(
    () => proseToHtml(this.lookFor()) || this.requirementsHtml(),
  );

  /** Creator List Values */

  // If the user uploads a CSV during the create brief flow, we need to send the URL to the API
  uploadedCsvUrl = signal<string | undefined>(undefined);
  selectedList = signal<ScreeningListResponseDto | undefined>(undefined);
  /** True when creators were already picked (e.g. from a brief assignment) before this flow. */
  usePreselectedCreators = signal(false);
  /** Creator List Values - END */

  /** Clears all draft state so a flow can start over from scratch. */
  reset(): void {
    this.basicInfoModel.set({ briefTitle: '' });
    this.selectedProduct.set(undefined);
    this.additionalProductImages.set([]);
    this.creativeMethod.set('type');
    this.requirementsModel.set({ requirementsGoogleDocUrl: '' });
    this.requirementsHtml.set('');
    this.requirementsDocumentUrl.set('');
    this.screenContentUntil.set(undefined);
    this.briefTextModel.set({ requirementsGoogleDocUrl: '' });
    this.briefTextHtml.set('');
    this.restrictionsModel.set({ restrictionsGoogleDocUrl: '' });
    this.restrictionsHtml.set('');
    this.restrictionsDocumentUrl.set('');
    this.uploadedCsvUrl.set(undefined);
    this.selectedList.set(undefined);
    this.usePreselectedCreators.set(false);
    this.lookFor.set('');
    this.restrictions.set([]);
    this.reportType.set(undefined);
    this.reportPrompt.set('');
    this.creatorSourceMethod.set(undefined);
    this.manualCreators.set([]);
    this.checkPassed.set(false);
  }

}
